"use client";

import { useQuery } from "@tanstack/react-query";
import { Flame, Leaf } from "lucide-react";
import Image from "next/image";
import { fetchMenuCategories, fetchMenuItems } from "@/lib/api";
import type { MenuCategory, MenuItem } from "@/types/restaurant";

function formatPrice(value: number | null | undefined): string {
  if (value == null) return "";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(value);
}

function MenuItemCard({ item }: { item: MenuItem }) {
  return (
    <div className="flex gap-4 py-4">
      {item.imageUrl && (
        <Image
          src={item.imageUrl}
          alt=""
          width={80}
          height={80}
          className="h-20 w-20 flex-shrink-0 rounded-lg object-cover"
          loading="lazy"
          unoptimized
        />
      )}
      <div className="flex-1">
        <div className="flex items-start justify-between gap-4">
          <h3 className="font-semibold text-slate-900">{item.name}</h3>
          {item.price != null && <span className="whitespace-nowrap font-semibold text-slate-900">{formatPrice(item.price)}</span>}
        </div>
        {item.description && <p className="mt-1 text-sm text-slate-600">{item.description}</p>}
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
          {item.spicyLevel > 0 && (
            <span className="inline-flex items-center gap-1 text-red-600">
              <Flame className="h-3 w-3" aria-hidden />
              {"\u2605".repeat(item.spicyLevel)}
            </span>
          )}
          {item.dietaryTags.map((tag) => (
            <span key={tag} className="inline-flex items-center gap-1 rounded bg-emerald-50 px-1.5 py-0.5 font-medium text-emerald-700">
              <Leaf className="h-3 w-3" aria-hidden />
              {tag}
            </span>
          ))}
          {item.allergens.map((allergen) => (
            <span key={allergen} className="rounded bg-amber-50 px-1.5 py-0.5 font-medium text-amber-700">
              Contains {allergen}
            </span>
          ))}
          {item.availabilityNote && <span className="text-slate-500">{item.availabilityNote}</span>}
        </div>
      </div>
    </div>
  );
}

function CategorySection({ category, items }: { category: MenuCategory; items: MenuItem[] }) {
  return (
    <section id={category.slug} className="border-b border-slate-200 py-8 last:border-b-0">
      <h2 className="text-2xl font-bold text-slate-900">{category.name}</h2>
      {category.description && <p className="mt-1 text-slate-600">{category.description}</p>}
      <div className="mt-4 divide-y divide-slate-100">
        {items.map((item) => <MenuItemCard key={item.id} item={item} />)}
      </div>
    </section>
  );
}

export function RestaurantMenu() {
  const categoriesQuery = useQuery({ queryKey: ["menu-categories"], queryFn: fetchMenuCategories });
  const itemsQuery = useQuery({ queryKey: ["menu-items"], queryFn: fetchMenuItems });
  const categories = categoriesQuery.data ?? [];
  const items = itemsQuery.data ?? [];

  if (categoriesQuery.isLoading || itemsQuery.isLoading) {
    return <p className="text-slate-600">Loading menu...</p>;
  }

  if (categoriesQuery.isError || itemsQuery.isError) {
    return <p role="alert" className="text-red-700">Could not load the menu right now.</p>;
  }

  const itemsByCategory = new Map<string, MenuItem[]>();
  for (const category of categories) {
    itemsByCategory.set(category.id, []);
  }
  for (const item of items) {
    if (item.categoryId && itemsByCategory.has(item.categoryId)) {
      itemsByCategory.get(item.categoryId)!.push(item);
    }
  }

  const featuredItems = items.filter((item) => item.featured && (!item.categoryId || !categories.some((category) => category.id === item.categoryId)));

  return (
    <div>
      {categories.length === 0 && items.length === 0 ? (
        <div className="border-y border-dashed border-slate-300 py-12 text-center">
          <p className="font-medium text-slate-800">Menu coming soon</p>
          <p className="mt-1 text-sm text-slate-500">Check back later for our latest dishes and drinks.</p>
        </div>
      ) : (
        <>
          {featuredItems.length > 0 && (
            <section className="mb-10 rounded-xl bg-amber-50 p-6">
              <h2 className="text-xl font-bold text-amber-900">Featured</h2>
              <div className="mt-4 divide-y divide-amber-100">
                {featuredItems.map((item) => <MenuItemCard key={item.id} item={item} />)}
              </div>
            </section>
          )}
          {categories.map((category) => {
            const categoryItems = itemsByCategory.get(category.id) ?? [];
            if (categoryItems.length === 0) return null;
            return <CategorySection key={category.id} category={category} items={categoryItems} />;
          })}
        </>
      )}
    </div>
  );
}
